import { Alert, AlertDescription, AlertIcon, Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { SpacingContainer } from '@resistapp/client/components/containers/spacing-container';
import { RSecondTitle } from '@resistapp/client/components/headers/r-second-title';
import { useSearchParamsContext } from '@resistapp/client/contexts/search-params-context';
import { useUser } from '@resistapp/client/contexts/use-user-context';
import { useVerifyEmail } from '@resistapp/client/hooks/api';
import { useLabWorkbookLanding } from '@resistapp/client/hooks/use-lab-workbook-landing';
import { useNavigateWithQuery } from '@resistapp/client/hooks/use-navigate-with-query';
import { OtherParams } from '@resistapp/client/utils/url-manipulation';
import { getUserProjectIds } from '@resistapp/common/access';
import { Feature, hasFeature } from '@resistapp/common/features';
import { useEffect, useMemo, useRef, useState } from 'react';
import { SignupLogo } from '../../components/icons/signup-logo';
import { getPreferredProjectId, shouldForceLabLanding } from '../../utils/access';
import { getBestPageForProject } from '../../utils/project-page-access';

const REDIRECT_COUNTDOWN_SECONDS = 5;

export function VerifyEmailView() {
  const { setLoggedIn } = useUser();
  const navigate = useNavigateWithQuery();
  const { getQueryParams } = useSearchParamsContext();
  const verificationCode = getQueryParams(OtherParams.VERIFICATION_CODE);
  const { create } = useVerifyEmail();
  const [redirectSeconds, setRedirectSeconds] = useState<number | undefined>();
  const countdownStartedRef = useRef(false);

  useEffect(() => {
    if (verificationCode) {
      create.mutate({ verificationCode });
    }
    // eslint-disable-next-line exhaustive-deps-with-refs/exhaustive-deps
  }, [verificationCode]);

  const verifiedUser = create.isSuccess ? create.data : null;
  const forceLabLanding = shouldForceLabLanding(verifiedUser);
  const { path: labLandingPath, organisationId: labOrganisationId, ensureQuery: labLandingQuery } =
    useLabWorkbookLanding(verifiedUser, { enabled: create.isSuccess && forceLabLanding });
  const labLandingPending = Boolean(forceLabLanding && labOrganisationId && labLandingQuery.isPending);

  useEffect(() => {
    if (create.isSuccess) {
      setLoggedIn(create.data);
    }
  }, [create.isSuccess, create.data, setLoggedIn]);

  const redirectTarget = useMemo(() => {
    if (!verifiedUser || labLandingPending) {
      return null;
    }
    if (forceLabLanding && labLandingPath) {
      return labLandingPath;
    }
    if (forceLabLanding) {
      return '/admin/lab-workbooks';
    }
    const projects = getUserProjectIds(verifiedUser);
    if (!projects.length) {
      if (hasFeature(verifiedUser, Feature.ADMIN)) {
        return '/admin';
      }
      return null;
    }
    const preferredProjectId = getPreferredProjectId(verifiedUser);
    if (!preferredProjectId) {
      return null;
    }
    return `/${getBestPageForProject(verifiedUser, preferredProjectId)}/${preferredProjectId}`;
  }, [verifiedUser, labLandingPath, labLandingPending, forceLabLanding]);

  useEffect(() => {
    if (!create.isSuccess || !redirectTarget || countdownStartedRef.current) {
      return;
    }

    countdownStartedRef.current = true;
    setRedirectSeconds(REDIRECT_COUNTDOWN_SECONDS);

    const intervalId = setInterval(() => {
      setRedirectSeconds(currentSeconds => {
        const nextSeconds =
          typeof currentSeconds === 'number'
            ? currentSeconds - 1
            : REDIRECT_COUNTDOWN_SECONDS - 1;

        if (nextSeconds <= 0) {
          clearInterval(intervalId);
          navigate(redirectTarget, false);
          return 0;
        }

        return nextSeconds;
      });
    }, 1000);

    return () => {
      countdownStartedRef.current = false;
      clearInterval(intervalId);
    };
  }, [create.isSuccess, navigate, redirectTarget]);

  return (
    <Flex direction="column" alignItems="center">
      <SpacingVerifyContainer>
        <SignupLogo />
        <RSecondTitle style={{ textAlign: 'center', paddingBottom: '30px' }}>Verify email</RSecondTitle>
        {create.error && (
          <Alert status="error">
            <AlertIcon />
            <AlertDescription>{create.error.message}</AlertDescription>
          </Alert>
        )}
        {create.isSuccess && (
          <Alert status="success">
            <AlertIcon />
            <AlertDescription>
              Email verified.
              <br />
              Redirecting you to the platform
              {typeof redirectSeconds === 'number' ? ` in ${redirectSeconds} s...` : '...'}
            </AlertDescription>
          </Alert>
        )}
      </SpacingVerifyContainer>
    </Flex>
  );
}

const SpacingVerifyContainer = styled(SpacingContainer)`
  max-width: 400px;
  width: 100%;
`;
